/**
 * IframeDetector - Global class for robust iframe detection
 * 
 * This class performs multiple checks to ensure accurate detection
 * of whether the application is running inside an iframe.
 * 
 * Multiple verification methods are used to prevent bypass attempts.
 */
class IframeDetector {
  constructor() {
    this._isInIframe = null;
    this._detectionMethods = [];
    this._performDetection();
  }

  /**
   * Performs multiple detection methods to determine if running in iframe
   * @private
   */
  _performDetection() {
    const results = [];

    // Method 1: Compare window.self with window.top
    try {
      const method1 = window.self !== window.top;
      results.push({ method: 'self !== top', result: method1 });
    } catch (e) {
      // Cross-origin access error means we're definitely in an iframe
      results.push({ method: 'self !== top', result: true, error: true });
    }

    // Method 2: Compare window.self with window.parent
    try {
      const method2 = window.self !== window.parent;
      results.push({ method: 'self !== parent', result: method2 });
    } catch (e) {
      results.push({ method: 'self !== parent', result: true, error: true });
    }

    // Method 3: Check window.frameElement
    try {
      const method3 = window.frameElement !== null && window.frameElement !== undefined;
      results.push({ method: 'frameElement', result: method3 });
    } catch (e) {
      // Cross-origin error indicates iframe
      results.push({ method: 'frameElement', result: true, error: true });
    }

    // Method 4: Check if window.top is accessible
    try {
      // Just trying to access - if it throws, we're in an iframe
      void window.top.location.href;
      results.push({ method: 'top.location accessible', result: false });
    } catch (e) {
      // Cannot access top.location = in iframe with different origin
      results.push({ method: 'top.location accessible', result: true, error: true });
    }

    // Method 5: Check parent location
    try {
      if (window.parent !== window.self) {
        // Just trying to access - if it throws, we're in an iframe
        void window.parent.location.href;
        results.push({ method: 'parent.location accessible', result: true });
      } else {
        results.push({ method: 'parent.location accessible', result: false });
      }
    } catch (e) {
      results.push({ method: 'parent.location accessible', result: true, error: true });
    }

    // Method 6: Check window.length (number of frames in window)
    try {
      const hasFrames = window.parent.frames.length > 0 && window.self !== window.parent;
      results.push({ method: 'parent.frames.length', result: hasFrames });
    } catch (e) {
      results.push({ method: 'parent.frames.length', result: true, error: true });
    }

    // Method 7: Check if window has been framed by checking ancestors
    try {
      const method7 = window !== window.top;
      results.push({ method: 'window !== top', result: method7 });
    } catch (e) {
      results.push({ method: 'window !== top', result: true, error: true });
    }

    // Store all detection results
    this._detectionMethods = results;

    // Consider it an iframe if ANY method detects it
    // This is the most secure approach - better safe than sorry
    this._isInIframe = results.some(r => r.result === true);
  }

  /**
   * Returns whether the application is running in an iframe
   * @returns {boolean} true if in iframe, false otherwise
   */
  isInIframe() {
    return this._isInIframe;
  }

  /**
   * Returns detailed detection results for debugging
   * @returns {Array} Array of detection method results
   */
  getDetectionDetails() {
    return this._detectionMethods;
  }

  /**
   * Re-runs the detection (useful if context changes)
   */
  redetect() {
    this._performDetection();
  }

  /**
   * Blocks execution if in iframe (security measure)
   * @param {string} message - Message to display
   * @throws {Error} If running in iframe
   */
  blockIfInIframe(message = 'This application cannot run inside an iframe') {
    if (this._isInIframe) {
      throw new Error(message);
    }
  }

  /**
   * Attempts to break out of iframe (use with caution)
   * WARNING: This will redirect the parent window if possible
   */
  breakOut() {
    if (this._isInIframe) {
      try {
        if (window.top.location !== window.location) {
          window.top.location = window.location;
        }
      } catch (e) {
        // Cannot break out due to cross-origin restrictions
        // Silent fail in production
      }
    }
  }

  /**
   * Returns information about the parent frame (if accessible)
   * @returns {Object|null} Parent frame info or null
   */
  getParentInfo() {
    if (!this._isInIframe) {
      return null;
    }

    const info = {
      accessible: false,
      origin: null,
      href: null,
      crossOrigin: false
    };

    try {
      info.href = window.parent.location.href;
      info.origin = window.parent.location.origin;
      info.accessible = true;
    } catch (e) {
      info.crossOrigin = true;
    }

    return info;
  }
}

// Create singleton instance
const iframeDetector = new IframeDetector();

// Make it globally available (for debugging and direct access)
if (typeof window !== 'undefined') {
  window.IframeDetector = iframeDetector;
}

// Note: We don't freeze the object because redetect() needs to update internal properties
// Security is maintained through the singleton pattern and closure of private methods

export default iframeDetector;

